<script setup lang="ts">
import { PropType } from 'vue';
import {
  ContainerTypes,
  Contexts,
  CurrentCaseServiceInterface,
  NamedBindings,
  TranslationService,
  ValuesSetValue,
  getContainer,
} from '@dandelion/core';

const props = defineProps({
  fullName: {
    type: String as PropType<string>,
    required: false,
    default: '',
  },
  context: {
    type: String as PropType<string>,
    required: false,
    default: Contexts.DEFAULT,
  },
  containerType: {
    type: String as PropType<ContainerTypes>,
    required: false,
    default: ContainerTypes.DEFAULT,
  },
});

const ts = getContainer(props.context).get(
  NamedBindings.TranslationService
) as TranslationService;
const currentCaseService = getContainer(props.context).get(
  NamedBindings.CurrentCaseService
) as CurrentCaseServiceInterface;

const proceduresColumns = [
  { label: '', minWidth: '3rem', width: '5%' },
  { label: 'Name', minWidth: '16rem', width: '22.5%' },
  { label: 'Present at the entrance', minWidth: '10rem', width: '15%' },
  { label: 'Start Date', minWidth: '8rem', width: '11%' },
  { label: 'End Date', minWidth: '8rem', width: '11%' },
  {
    label: 'Discharge with aid and/or treatment still present',
    minWidth: '4rem',
    width: '30%',
  },
  { label: '', minWidth: '3rem', width: '5.5%' },
];

const surgicalsColumns = [
  { label: '', width: '2rem' },
  { label: 'Name', width: '10rem' },
  { label: 'Date', width: '6rem' },
];

const rebleedingColumns = [
  { label: '', width: '5rem' },
  { label: 'Date rebleeding', width: '20rem' },
  { label: 'Time rebleeding', width: '20rem' },
  { label: 'Timing rebleeding', width: '25rem' },
];

const FlowDiagramColumns = [
  { label: '', width: '2rem' },
  { label: 'Type procedure', width: '10rem' },
  { label: 'Date procedure', width: '6rem' },
  { label: 'Time procedure', width: '6rem' },
];

const vasospasmInterventionsEsa = [
  { label: '', width: '2rem' },
  { label: 'Type interventions', width: '18rem' },
  { label: 'Date procedure', width: '2rem' },
  { label: 'hour procedure', width: '2rem' },
  { label: 'Complications', width: '2rem' },
  { label: 'Effect on vasospasm', width: '2rem' },
];

const persidiTrattamentiTrauma = [
  { label: 'Type', width: '16rem' },
  { label: 'Treatment start date', width: '5rem' },
  { label: 'End date of treatment', width: '5rem' },
  { label: 'Time of treatment', width: '5rem' },
];

function processValue(value: any): string {
  console.log('processValue', value);
  if (value === '' || value == null) {
    return `<span class=\"value\">${ts.t('Empty value')}</span>`;
  }
  // Valore di un ValuesSetValue
  if (value instanceof ValuesSetValue) {
    return `<span class=\"value\">${ts.t(value.getLabel())}</span>`;
  }
  // Array
  if (Array.isArray(value)) {
    return value.map((item) => processValue(item)).join(' ');
  }
  // Oggetto con attributi
  if (typeof value === 'object') {
    // Se ha .value e .label
    if ('value' in value && 'label' in value) {
      return `<span class=\"value\">${ts.t(value.label)}</span>`;
    }
    // Altrimenti stringify
    return `<span>${ts.t(JSON.stringify(value))}</span>`;
  }
  // Stringa or number
  console.log('stringa', value.toString());
  return `<span class=\"value\">${value.toString()}</span>`;
}
const tableDateAndHoure = [
  { label: '47654', width: '3rem' },
  { label: '47655', width: '5rem' },
];

function checkIfProcedureList() {
  //This if is used to avoid loading the model every time this function is called
  if (props.fullName === 'core.proceduresTreatments.proceduresList') {
    const model = currentCaseService.getModel();
    return model.core?.proceduresTreatments?.procedures?.length > 0;
  }
}

function checkIfSurgicalOrNonSurgicalList() {
  if (props.fullName === 'core.surgicalInterventions.surgicalList') {
    const model = currentCaseService.getModel();
    return (
      model.core?.surgicalInterventions?.surgicalNoneOption?.value ===
      'yesNoCodification.yes'
    );
  } else if (
    props.fullName === 'core.nonSurgicalInterventions.nonSurgicalList'
  ) {
    const model = currentCaseService.getModel();
    return (
      model.core?.nonSurgicalInterventions?.nonSurgicalNoneOption?.value ===
      'yesNoCodification.yes'
    );
  }
}

function checkIfrebleedingEsa() {
  if (props.fullName === 'esa.rebleedingPage.rebleedingEsa') {
    const model = currentCaseService.getModel();
    return model.esa?.rebleedingPage?.rebleedingEsa;
  }
}

function checkIfFlowDiagram() {
  if (props.fullName === 'esa.flowDiagramPage.flowDiagram') {
    const model = currentCaseService.getModel();
    return model.esa?.flowDiagramPage?.flowDiagram;
  }
}

function checkIFvasospasmInterventionsEsa() {
  if (
    props.fullName ===
    'esa.radiologicalInterventionsVasospasm.vasospasmInterventionsEsa'
  ) {
    const model = currentCaseService.getModel();
    return (
      model.esa?.radiologicalInterventionsVasospasm?.vasoradintervEsa?.value ==
      'yesNoEsaCodification.esaYes'
    );
  }
}

function checkIfpersidiTrattamentiTrauma() {
  //This if is used to avoid loading the model every time this function is called
  if (props.fullName === 'trauma.terapiaIntensiva.persidiTrattamentiTrauma') {
    const model = currentCaseService.getModel();
    const persidiTrattamentiTrauma =
      model.trauma?.terapiaIntensiva?.persidiTrattamentiTrauma;
    return (
      persidiTrattamentiTrauma &&
      (persidiTrattamentiTrauma.ecmo?.length > 0 ||
        persidiTrattamentiTrauma.monitoraggioICPSenzaLiquor?.length > 0 ||
        persidiTrattamentiTrauma.monitoraggioICPLiquor?.length > 0 ||
        persidiTrattamentiTrauma.drenaggioVentricolareEsterno?.length > 0)
    );
  }
  return false;
}

// Colonne per output ISS
type IssColumn = { key: string; label: string };
const outputISSColumns: IssColumn[] = [
  { key: 'ISSHeadNeckTrauma', label: '2367' },
  { key: 'ISSFaceTrauma', label: '2368' },
  { key: 'ISSChestTrauma', label: '2369' },
  { key: 'ISSAbdomenPelvicContentsTrauma', label: '2370' },
  { key: 'ISSExtremitiesPelvicGirdleTrauma', label: '2371' },
  { key: 'ISSExternalTrauma', label: '2372' },
  { key: 'ISSTrauma', label: '2326' },
];

function checkIfoutputISS() {
  return props.fullName === 'trauma.traumaGravità.outputISS';
}

function getOutputISSGroup() {
  const model = currentCaseService.getModel();
  return model.trauma?.traumaGravità?.outputISS || {};
}

function getOutputValueHtml(key: string): string {
  const group = getOutputISSGroup();
  const rawValue = (group as any)[key]?.value ?? (group as any)[key];
  console.log('rawValue', rawValue);
  // Se il valore è un oggetto con la proprietà "value", estrai solo il valore
  if (rawValue && typeof rawValue === 'object' && 'value' in rawValue) {
    return processValue(rawValue);
  }
  return processValue(rawValue);
}

const chirurgia = [
  { label: 'Type', width: '16rem' },
  { label: 'Date of intervention', width: '5rem' },
  { label: 'Is this an emergency intervention', width: '5rem' },
  { label: 'Time of intervention', width: '5rem' },
];

function checkIfchirurgiaAddominale() {
  //This if is used to avoid loading the model every time this function is called
  if (props.fullName === 'trauma.chirurgiaEmergenza.chirurAdd') {
    const model = currentCaseService.getModel();
    const chirurAdd = model.trauma?.chirurgiaEmergenza?.chirurAdd;
    return (
      chirurAdd &&
      (chirurAdd.chirurgiaGastrointestinale?.length > 0 ||
        chirurAdd.chirurgiaEpatica?.length > 0 ||
        chirurAdd.chirurgiaVieBiliari?.length > 0)
    );
  }
  return false;
}

function checkIfchirurgiaVascolare() {
  //This if is used to avoid loading the model every time this function is called
  if (props.fullName === 'trauma.chirurgiaEmergenza.chirurVasc') {
    const model = currentCaseService.getModel();
    const chirurVasc = model.trauma?.chirurgiaEmergenza?.chirurVasc;
    return (
      chirurVasc &&
      (chirurVasc.chirurgiaVacolareToracica?.length > 0 ||
        chirurVasc.chirurgiaVascolareAddominale?.length > 0 ||
        chirurVasc.chirurgiaVascolarePeriferica?.length > 0)
    );
  }
  return false;
}

function checkIfMchirurgiaMaxillofacciale() {
  //This if is used to avoid loading the model every time this function is called
  if (props.fullName === 'trauma.chirurgiaEmergenza.chirurgiaMaxillofacciale') {
    const model = currentCaseService.getModel();
    return (
      model.trauma?.chirurgiaEmergenza?.chirurgiaMaxillofacciale?.length > 0
    );
  }
}

function checkIfMchirurgiaOftalmica() {
  //This if is used to avoid loading the model every time this function is called
  if (props.fullName === 'trauma.chirurgiaEmergenza.chirurgiaOftalmica') {
    const model = currentCaseService.getModel();
    return model.trauma?.chirurgiaEmergenza?.chirurgiaOftalmica?.length > 0;
  }
}

function checkIfchirurgiaNefrourologica() {
  //This if is used to avoid loading the model every time this function is called
  if (props.fullName === 'trauma.chirurgiaEmergenza.chirurgiaNefrourologica') {
    const model = currentCaseService.getModel();
    return (
      model.trauma?.chirurgiaEmergenza?.chirurgiaNefrourologica?.length > 0
    );
  }
}

function checkIfcardChiru() {
  //This if is used to avoid loading the model every time this function is called
  if (props.fullName === 'trauma.chirurgiaEmergenza.cardChiru') {
    const model = currentCaseService.getModel();
    const cardChiru = model.trauma?.chirurgiaEmergenza?.cardChiru;
    return (
      cardChiru &&
      (cardChiru.chirurgiaPatologicaVascolareAcquisita?.length > 0 ||
        cardChiru.chirurgiaAortaToracica?.length > 0 ||
        cardChiru.altraCardiochirurgia?.length > 0)
    );
  }
  return false;
}

function checkIfChirurgiaORL() {
  //This if is used to avoid loading the model every time this function is called
  if (props.fullName === 'trauma.chirurgiaEmergenza.ChirurgiaORL') {
    const model = currentCaseService.getModel();
    return model.trauma?.chirurgiaEmergenza?.ChirurgiaORL?.length > 0;
  }
}

function checkIfchirurgiaTor() {
  //This if is used to avoid loading the model every time this function is called
  if (props.fullName === 'trauma.chirurgiaEmergenza.chirurgiaTor') {
    const model = currentCaseService.getModel();
    const chirurgiaTor = model.trauma?.chirurgiaEmergenza?.chirurgiaTor;
    return (
      chirurgiaTor &&
      (chirurgiaTor.chirurgiaToracica?.length > 0 ||
        chirurgiaTor.chirurgiaEsofagea?.length > 0)
    );
  }
  return false;
}

const neuroEOrtop = [
  { label: 'Type', width: '16rem' },
  { label: 'Date of intervention', width: '5rem' },
  { label: 'Is this an emergency intervention', width: '5rem' },
];
const nonchirurgia = [
  { label: 'Type', width: '16rem' },
  { label: 'Date of intervention', width: '5rem' },
  { label: 'Time of intervention', width: '5rem' },
];

function checkIfchirurgiaOrtopedica() {
  //This if is used to avoid loading the model every time this function is called
  if (props.fullName === 'trauma.chirurgiaEmergenza.chirurgiaOrtopedica') {
    const model = currentCaseService.getModel();
    return model.trauma?.chirurgiaEmergenza?.chirurgiaOrtopedica?.length > 0;
  }
}

function checkIfchirurgiaNeurochirugica() {
  //This if is used to avoid loading the model every time this function is called
  if (props.fullName === 'trauma.chirurgiaEmergenza.chirurgiaNeurochirugica') {
    const model = currentCaseService.getModel();
    return (
      model.trauma?.chirurgiaEmergenza?.chirurgiaNeurochirugica?.length > 0
    );
  }
}

function checkIfprocNonChiru() {
  //This if is used to avoid loading the model every time this function is called
  if (props.fullName === 'trauma.chirurgiaEmergenza.procNonChiru') {
    const model = currentCaseService.getModel();
    const procNonChiru = model.trauma?.chirurgiaEmergenza?.procNonChiru;
    return (
      procNonChiru &&
      (procNonChiru.radiologiaInterventistica?.length > 0 ||
        procNonChiru.cardiologiaInterventistica?.length > 0 ||
        procNonChiru.neuroradiologiaInterventistica?.length > 0 ||
        procNonChiru.endoscopiaInterventistica?.length > 0)
    );
  }
  return false;
}

// Colonne per output ISS
type TITraumaColon = { key: string; label: string };
const tableTITrauma: TITraumaColon[] = [
  { key: 'dataDimissioniTi', label: 'Date of discharge from ICU' },
  { key: 'oraDimissioniTi', label: 'Time of discharge from IT' },
  { key: 'esitoTi', label: 'ICU outcome' },
];

function checkIfoutputTITrauma() {
  return props.fullName === 'trauma.esito.tableTITrauma';
}

function getOutputTITraumaGroup() {
  const model = currentCaseService.getModel();
  return model.trauma?.esito?.tableTITrauma || {};
}

function getOutputTITrauma(key: string): string {
  const group = getOutputTITraumaGroup();
  const rawValue = (group as any)[key]?.value ?? (group as any)[key];
  return processValue(rawValue);
}

type outputGcsSapsiiColon = { key: string; label: string };
const outputGcsSapsii: outputGcsSapsiiColon[] = [
  { key: 'gcsAmmissioneTI', label: '13374' },
  { key: 'peggiorPF', label: '13447' },
  { key: 'peggiorPAS', label: '13378' },
];

function checkIfoutputGcsSapsii() {
  return props.fullName === 'trauma.terapiaIntensiva.outputGcsSapsii';
}

function getoutputGcsSapsiiGroup() {
  const model = currentCaseService.getModel();
  return model.trauma?.terapiaIntensiva?.outputGcsSapsii || {};
}

function getoutputGcsSapsii(key: string): string {
  const group = getoutputGcsSapsiiGroup();
  const rawValue = (group as any)[key]?.value ?? (group as any)[key];
  return processValue(rawValue);
}

type outputSeasHospEveHospWeekColon = { key: string; label: string };
const SeasHospEveHospWeek: outputSeasHospEveHospWeekColon[] = [
  { key: 'seasonEsa', label: '47992' },
  { key: 'hospEveAcc', label: '48001' },
  { key: 'hospWeekendAcc', label: '48002' },
];

function checkIftableSeasHospEveHospWeek() {
  return props.fullName === 'esa.prehospitalPhase.tableSeasHospEveHospWeek';
}

function getoutputSeasHospEveHospWeekGroup() {
  const model = currentCaseService.getModel();
  return model.esa?.prehospitalPhase?.tableSeasHospEveHospWeek || {};
}

function getoutputSeasHospEveHospWeek(key: string): string {
  const group = getoutputSeasHospEveHospWeekGroup();
  const rawValue = (group as any)[key]?.value ?? (group as any)[key];
  return rawValue;
}

type outputtableGCSPupilsColon = { key: string; label: string };
const tableGCSPupils: outputtableGCSPupilsColon[] = [
  { key: 'gcsFromCoreEsa', label: '9006' },
  { key: 'PupiliInitEsa', label: '2222241' },
  { key: 'deficitfocaleInitEsa', label: '2222242' },
  { key: 'hunthessInit', label: '2222466' },
  { key: 'wfnsInitEsa', label: '2222465' },
];

function checkIftableGCSPupils() {
  return props.fullName === 'esa.scoreProcedure.tableAdmissionScore';
  return props.fullName === 'esa.scoreProcedure.tableAdmissionScore';
}

function getoutputtableGCSPupilsGroup() {
  const model = currentCaseService.getModel();
  return model.esa?.scoreProcedure?.tableAdmissionScore || {};
  return model.esa?.scoreProcedure?.tableAdmissionScore || {};
}

function getoutputtableGCSPupils(key: string): string {
  const group = getoutputtableGCSPupilsGroup();
  const rawValue = (group as any)[key]?.value ?? (group as any)[key];
  return rawValue;
}

function checkIfTableDateAndHoure() {
  if (
    props.fullName ===
    'esa.proceduralPhaseAneurysmStabilisationType.tableDateAndHoure'
  ) {
    const model = currentCaseService.getModel();
    return model.esa?.proceduralPhaseAneurysmStabilisationType
      ?.tableDateAndHoure;
  }
}

type outputpreProcAdmCorrTableColon = { key: string; label: string };
const preProcAdmCorrTable: outputpreProcAdmCorrTableColon[] = [
  { key: 'gcsAdmPreEsa', label: '2222252' },
  { key: 'deficitfocaleAdmPreEsa', label: '2222253' },
  { key: 'wfnsAdmPreEsa', label: '2222254' },
  { key: 'hunthessAdmPreEsa', label: '2222255' },
];

function checkIfpreProcAdmCorrTable() {
  const model = currentCaseService.getModel();
  return props.fullName === 'esa.scoreProcedure.preProcAdmCorrTable' && 
  model.esa?.timingProcedure?.preProcAdmCorr?.value === 'yesNoEsaCodification.esaYes';
}

function getoutputpreProcAdmCorrTableGroup() {
  const model = currentCaseService.getModel();
  return model.esa?.scoreProcedure?.preProcAdmCorrTable || {};
}

function getoutputpreProcAdmCorrTable(key: string): string {
  const group = getoutputpreProcAdmCorrTableGroup();
  const rawValue = (group as any)[key]?.value ?? (group as any)[key];
  return rawValue;
}

type outputpostAdmCorrTableColon = { key: string; label: string };
const postAdmCorrTable: outputpostAdmCorrTableColon[] = [
  { key: 'gcsAdmPostEsa', label: '2222252' },
  { key: 'deficitfocaleAdmPostEsa', label: '2222253' },
  { key: 'wfnsAdmPostEsa', label: '2222254' },
  { key: 'hunthessAdmPostEsa', label: '2222255' },
];

function checkIfpostAdmCorrTable() {
  const model = currentCaseService.getModel();
  return props.fullName === 'esa.scoreProcedure.postAdmCorrTable' && 
  model.esa?.timingProcedure?.postAdmCorr?.value === 'yesNoEsaCodification.esaYes';
}

function getoutputpostAdmCorrTableGroup() {
  const model = currentCaseService.getModel();
  return model.esa?.scoreProcedure?.postAdmCorrTable || {};
}

function getoutputpostAdmCorrTable(key: string): string {
  const group = getoutputpostAdmCorrTableGroup();
  const rawValue = (group as any)[key]?.value ?? (group as any)[key];
  return rawValue;
}

type outPutComputedTomoGroupColon = { key: string; label: string };
const outPutComputedTomoGroupTable: outPutComputedTomoGroupColon[] = [
  { key: 'fishertEsa', label: '2222470' },
  { key: 'modFisherEsa', label: '2222471' },
  { key: 'claassenEsa', label: '2222472' },
];

function checkIfoutPutComputedTomoGroup() {
  const model = currentCaseService.getModel();
  return (
    props.fullName ===
      'esa.computedTomographyVasoRisk.outPutComputedTomoGroup' &&
    (model.esa?.computedTomographyVasoRisk?.subarhemorEsa?.value ===
      'yesNoTCCodification.positivOnly' ||
      model.esa?.computedTomographyVasoRisk?.subarhemorEsa?.value ===
        'yesNoTCCodification.yesEsa')
  );
}

function outPutComputedTomoGroup() {
  const model = currentCaseService.getModel();
  return model.esa?.computedTomographyVasoRisk?.outPutComputedTomoGroup || {};
}

function outPutComputedTomo(key: string): string {
  const group = outPutComputedTomoGroup();
  const rawValue = (group as any)[key]?.value ?? (group as any)[key];
  return rawValue;
}

type tableICHScoreColon = { key: string; label: string };
const tableICHScoreTable: tableICHScoreColon[] = [
  { key: 'GCSScoreReadonly', label: '441235' },
  { key: 'ICHVolumeReadonly', label: '441068' },
  { key: 'ichAgePatientReadonly', label: '441236' },
  { key: 'infraOriginICHReadonly', label: '441233' },
  { key: 'IVHVarReadonly', label: '441234' },
];

function checkIftableICHScore() {
  return props.fullName === 'ich.ICHScore.tableICHScore';
}

function getoutputtableICHScoreGroup() {
  const model = currentCaseService.getModel();
  return model.ich?.ICHScore?.tableICHScore || {};
}

function getoutputtableICHScore(key: string): string {
  const group = getoutputtableICHScoreGroup();
  const rawValue = (group as any)[key]?.value ?? (group as any)[key];
  return rawValue;
}

type tableOnSetColon = { key: string; label: string };
const tableOnSetTable: tableOnSetColon[] = [
  { key: 'OnsetToNeedle', label: '20148' },
  { key: 'OnsetToRecanalization', label: '20192' },
  { key: 'GroinToRecanalization', label: '20193' },
];

function checkIftableOnSet() {
  return props.fullName === 'stroke.riperfusione.groupOnset';
}

function getoutputtableOnSetGroup() {
  const model = currentCaseService.getModel();
  return model.stroke?.riperfusione?.groupOnset || {};
}

function getoutputtableOnSet(key: string): string {
  const group = getoutputtableOnSetGroup();
  const rawValue = (group as any)[key]?.value ?? (group as any)[key];
  return rawValue;
}
</script>

<template>
  <table v-if="checkIfProcedureList()">
    <thead>
      <tr>
        <th
          v-for="col in proceduresColumns"
          :style="{ minWidth: col.minWidth, width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </thead>
  </table>

  <table v-if="checkIfSurgicalOrNonSurgicalList()">
    <tbody>
      <tr>
        <th
          v-for="col in surgicalsColumns"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfrebleedingEsa()">
    <tbody>
      <tr>
        <th
          v-for="col in rebleedingColumns"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfFlowDiagram()">
    <tbody>
      <tr>
        <th
          v-for="col in FlowDiagramColumns"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIFvasospasmInterventionsEsa()">
    <tbody>
      <tr>
        <th
          v-for="col in vasospasmInterventionsEsa"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfpersidiTrattamentiTrauma()">
    <tbody>
      <tr>
        <th
          v-for="col in persidiTrattamentiTrauma"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <!-- Tabella ISS Output migliorata -->
  <table v-if="checkIfoutputISS()" class="iss-output-table">
    <tbody>
      <tr v-for="col in outputISSColumns" :key="col.key">
        <th class="label-column">{{ ts.t(col.label) }}</th>
        <td
          class="value-column"
          v-html="ts.t(getOutputValueHtml(col.key))"
        ></td>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfchirurgiaAddominale()">
    <tbody>
      <tr>
        <th
          v-for="col in chirurgia"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfchirurgiaVascolare()">
    <tbody>
      <tr>
        <th
          v-for="col in chirurgia"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfMchirurgiaMaxillofacciale()">
    <tbody>
      <tr>
        <th
          v-for="col in chirurgia"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfMchirurgiaOftalmica()">
    <tbody>
      <tr>
        <th
          v-for="col in chirurgia"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfchirurgiaNefrourologica()">
    <tbody>
      <tr>
        <th
          v-for="col in chirurgia"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfcardChiru()">
    <tbody>
      <tr>
        <th
          v-for="col in chirurgia"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfChirurgiaORL()">
    <tbody>
      <tr>
        <th
          v-for="col in chirurgia"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfchirurgiaTor()">
    <tbody>
      <tr>
        <th
          v-for="col in chirurgia"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfchirurgiaOrtopedica()">
    <tbody>
      <tr>
        <th
          v-for="col in neuroEOrtop"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfchirurgiaNeurochirugica()">
    <tbody>
      <tr>
        <th
          v-for="col in neuroEOrtop"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfprocNonChiru()">
    <tbody>
      <tr>
        <th
          v-for="col in nonchirurgia"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfoutputTITrauma()" class="TITrauma-output-table">
    <thead>
      <tr>
        <th v-for="col in tableTITrauma" :key="col.key" class="label-column">
          {{ ts.t(col.label) }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td
          v-for="col in tableTITrauma"
          :key="col.key"
          class="value-column"
          v-html="ts.t(getOutputTITrauma(col.key))"
        ></td>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfoutputGcsSapsii()" class="outputGcsSapsii-output-table">
    <thead>
      <tr>
        <th v-for="col in outputGcsSapsii" :key="col.key" class="label-column">
          {{ ts.t(col.label) }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td
          v-for="col in outputGcsSapsii"
          :key="col.key"
          class="value-column"
          v-html="getoutputGcsSapsii(col.key)"
        ></td>
      </tr>
    </tbody>
  </table>

  <table
    v-if="checkIftableSeasHospEveHospWeek()"
    class="SeasHospEveHospWeek-output-table"
  >
    <thead>
      <tr>
        <th
          v-for="col in SeasHospEveHospWeek"
          :key="col.key"
          class="label-column"
        >
          {{ ts.t(col.label) }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td
          v-for="col in SeasHospEveHospWeek"
          :key="col.key"
          class="value-column"
          v-html="ts.t(getoutputSeasHospEveHospWeek(col.key))"
        ></td>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIftableGCSPupils()" class="tableGCSPupils-output-table">
    <thead>
      <tr>
        <th v-for="col in tableGCSPupils" :key="col.key" class="label-column">
          {{ ts.t(col.label) }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td
          v-for="col in tableGCSPupils"
          :key="col.key"
          class="value-column"
          v-html="getoutputtableGCSPupils(col.key)"
        ></td>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfTableDateAndHoure()">
    <tbody>
      <tr>
        <th
          v-for="col in tableDateAndHoure"
          :style="{ width: col.width }"
          v-html="ts.t(col.label)"
        ></th>
      </tr>
    </tbody>
  </table>

  <table
    v-if="checkIfpreProcAdmCorrTable()"
    class="preProcAdmCorrTable-output-table"
  >
    <thead>
      <tr>
        <th
          v-for="col in preProcAdmCorrTable"
          :key="col.key"
          class="label-column"
        >
          {{ ts.t(col.label) }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td
          v-for="col in preProcAdmCorrTable"
          :key="col.key"
          class="value-column"
          v-html="getoutputpreProcAdmCorrTable(col.key)"
        ></td>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIfpostAdmCorrTable()" class="postAdmCorrTable-output-table">
    <thead>
      <tr>
        <th v-for="col in postAdmCorrTable" :key="col.key" class="label-column">
          {{ ts.t(col.label) }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td
          v-for="col in postAdmCorrTable"
          :key="col.key"
          class="value-column"
          v-html="getoutputpostAdmCorrTable(col.key)"
        ></td>
      </tr>
    </tbody>
  </table>

  <table
    v-if="checkIfoutPutComputedTomoGroup()"
    class="outPutComputedTomoGroupTable-output-table"
  >
    <thead>
      <tr>
        <th
          v-for="col in outPutComputedTomoGroupTable"
          :key="col.key"
          class="label-column"
        >
          {{ ts.t(col.label) }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td
          v-for="col in outPutComputedTomoGroupTable"
          :key="col.key"
          class="value-column"
          v-html="outPutComputedTomo(col.key)"
        ></td>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIftableICHScore()" class="tableICHScoreTable-output-table">
    <thead>
      <tr>
        <th
          v-for="col in tableICHScoreTable"
          :key="col.key"
          class="label-column"
        >
          {{ ts.t(col.label) }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td
          v-for="col in tableICHScoreTable"
          :key="col.key"
          class="value-column"
          v-html="getoutputtableICHScore(col.key)"
        ></td>
      </tr>
    </tbody>
  </table>

  <table v-if="checkIftableOnSet()" class="tableOnSetTable-output-table">
    <thead>
      <tr>
        <th v-for="col in tableOnSetTable" :key="col.key" class="label-column">
          {{ ts.t(col.label) }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td
          v-for="col in tableOnSetTable"
          :key="col.key"
          class="value-column"
          v-html="getoutputtableOnSet(col.key)"
        ></td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped>
.iss-output-table {
  width: 40%;
  table-layout: fixed;
}
</style>

<style>
.postAdmCorrTable-output-table,
.preProcAdmCorrTable-output-table {
  grid-column: 1;
}

.tableGCSPupils-output-table {
  grid-column: 1 / -1;
}
</style>
